import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DocStatusBadge, ServerStatusBadge } from "@/components/DocStatus";
import { NotaryServiceCounties } from "@/components/server/NotaryServiceCounties";
import { SigningAgentCard } from "@/components/server/SigningAgentCard";
import { NotaryAlertsToggle } from "@/components/server/NotaryAlertsToggle";
import { CheckCircle2, AlertCircle, Upload, Eye, Download, FileCheck2, ShieldCheck, FileSignature, CreditCard, Lock, Clock } from "lucide-react";

const STEP_ICON = { complete: CheckCircle2, in_progress: Clock, locked: Lock, coming_soon: Clock };

function StepIndicator({ steps }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="onboarding-steps">
      {steps.map((st, i) => {
        const Icon = STEP_ICON[st.status] || Clock;
        const tone = st.status === "complete" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : st.status === "in_progress" ? "border-indigo-200 bg-indigo-50 text-indigo-700"
          : st.status === "coming_soon" ? "border-slate-200 bg-slate-50 text-slate-400"
          : "border-slate-200 bg-white text-slate-400";
        return (
          <div key={st.key} className="flex items-center gap-2">
            <span data-testid={`onboarding-step-${st.key}`} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${tone}`}>
              <Icon className="h-3.5 w-3.5" /> {st.label}
              {st.status === "coming_soon" && <span className="ml-1 rounded bg-slate-200 px-1 text-[9px] uppercase tracking-wide text-slate-500">Soon</span>}
            </span>
            {i < steps.length - 1 && <span className="text-slate-300">→</span>}
          </div>
        );
      })}
    </div>
  );
}

function ProgressBar({ value, total, testid }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100" data-testid={testid}>
      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

// Best-effort parse of a stored "street, city, STATE zip" address back into parts (for edit reload).
function parseAddress(a) {
  const out = { street: "", city: "", state: "", zip: "" };
  if (!a) return out;
  const parts = a.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) {
    out.street = parts[0];
    out.city = parts.slice(1, -1).join(", ");
    const m = parts[parts.length - 1].match(/([A-Za-z]{2})\s+(\S+)/);
    if (m) { out.state = m[1].toUpperCase(); out.zip = m[2]; } else { out.state = parts[parts.length - 1]; }
  } else {
    out.street = a;
  }
  return out;
}

export default function ServerOnboarding() {
  const { refresh, user } = useAuth();
  const [data, setData] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [profile, setProfile] = useState({ name: "", phone: "", street: "", city: "", state: "", zip: "", license_number: "", coverage_counties: "" });
  const fileRefs = useRef({});
  const [agr, setAgr] = useState(null);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [icSig, setIcSig] = useState("");
  const [signingIc, setSigningIc] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [applying, setApplying] = useState("");
  const [execAgr, setExecAgr] = useState(null);

  const loadExecAgr = async () => {
    try { const { data } = await api.get("/user-agreement/executed"); setExecAgr(data); }
    catch (e) { toast.error("Could not load your executed agreement."); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/server/onboarding");
      setData(data);
      try { const a = await api.get("/server/provider-agreement"); setAgr(a.data); } catch { /* noop */ }
      const parsed = parseAddress(data.account.address || "");
      setProfile({
        name: data.account.name || "", phone: data.account.phone || "",
        coverage_counties: (data.account.coverage_counties || []).join(", "),
        street: parsed.street, city: parsed.city, state: parsed.state, zip: parsed.zip,
        license_number: data.account.license_number || "",
      });
    } catch (e) { toast.error("Failed to load onboarding"); }
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    const { name, phone, street, city, state, zip } = profile;
    if (!name.trim() || !phone.trim() || !street.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      toast.error("Full legal name, phone, street address, city, state, and ZIP code are all required");
      return;
    }
    setSavingProfile(true);
    try {
      const address = `${street.trim()}, ${city.trim()}, ${state.trim()} ${zip.trim()}`;
      await api.post("/auth/profile", { name: name.trim(), phone: phone.trim(), address, license_number: profile.license_number, coverage_counties: profile.coverage_counties.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Profile updated");
      await load();
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const upload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/server/documents/${key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document submitted for review");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingKey(null); }
  };

  if (!data) return <p className="text-slate-400">Loading…</p>;

  const signIc = async () => {
    if (!agreeChecked) { toast.error("Please check the box to agree before continuing"); return; }
    if (!icSig.trim()) { toast.error("Type your full legal name to sign"); return; }
    setSigningIc(true);
    try {
      await api.post("/server/provider-agreement/accept", { agree: true, signature: icSig.trim() });
      toast.success("ARPS Provider Service Agreement accepted");
      await load();
      if (refresh) await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to accept agreement"); }
    finally { setSigningIc(false); }
  };

  const downloadAgreement = async () => {
    try {
      const res = await api.get("/server/provider-agreement/document", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "ARPS-Provider-Agreement.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download your agreement copy."); }
  };

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/server/connect/onboard", {});
      if (data.url) { window.location.assign(data.url); return; }
      toast.error("Could not start payout setup");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payout setup"); }
    finally { setConnecting(false); }
  };

  const applyService = async (service) => {
    setApplying(service);
    try {
      const { data: r } = await api.post("/server/apply-service", { service });
      toast.success(r.message || "Applied");
      await load();
      if (refresh) await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not apply"); }
    finally { setApplying(""); }
  };

  const s = data.summary;
  const accountComplete = data.account.complete;
  const cn = data.connect || {};

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="server-onboarding">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Onboarding & document center</h2>
          <p className="text-slate-500">Complete your profile and submit required credentials. An ARPS admin reviews every document.</p>
        </div>
      </div>

      {/* Service applications — reuses POST /api/server/apply-service (no duplicate account) */}
      {(() => {
        const cap = user?.service_capability || "process_server";
        const rejected = data.server_status === "rejected";
        const svcStatus = (svc, eligible) => {
          const applied = cap === "both" || cap === svc;
          if (!applied) return { label: "Not Applied", tone: "border-slate-200 bg-slate-50 text-slate-500" };
          if (eligible) return { label: "Approved", tone: "border-emerald-200 bg-emerald-50 text-emerald-700" };
          if (rejected) return { label: "Rejected", tone: "border-red-200 bg-red-50 text-red-600" };
          return { label: "Pending", tone: "border-amber-200 bg-amber-50 text-amber-700" };
        };
        const services = [
          { key: "process_server", title: "Process Serving", eligible: !!user?.process_server_eligible },
          { key: "notary", title: "Notary", eligible: !!user?.notary_eligible },
        ];
        return (
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="service-applications-card">
            <h3 className="font-display text-lg font-bold text-slate-900">Service applications</h3>
            <p className="text-sm text-slate-500">Apply for the services you want to provide. Your account is reused — upload each service's credentials below for admin review.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {services.map((svc) => {
                const st = svcStatus(svc.key, svc.eligible);
                const applied = cap === "both" || cap === svc.key;
                return (
                  <div key={svc.key} data-testid={`service-app-${svc.key}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                    <div>
                      <p className="font-semibold text-slate-800">{svc.title}</p>
                      <span data-testid={`service-app-status-${svc.key}`} className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${st.tone}`}>{st.label}</span>
                    </div>
                    {!applied && (
                      <Button size="sm" data-testid={`apply-service-${svc.key}`} disabled={applying === svc.key} onClick={() => applyService(svc.key)} className="bg-indigo-600 text-white hover:bg-indigo-700">
                        {applying === svc.key ? "Applying…" : "Apply"}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Service counties (optional for all providers). Notaries see "preferred" wording.
          Never a required approval step. */}
      <div className="space-y-4">
        <NotaryServiceCounties
          title={["notary", "both"].includes(user?.service_capability) ? "Counties you prefer to serve" : "Counties you serve"}
          subtitle={["notary", "both"].includes(user?.service_capability)
            ? <>Optional. Tell us the counties you prefer for notary work so those jobs reach you first. Leaving this empty means <span className="font-medium text-slate-600">no preference</span> — you'll still see and can claim any job.</>
            : <>Optional. Add the counties where you serve process so local jobs reach you first. Leaving this empty means <span className="font-medium text-slate-600">no preference</span> — you'll still see and can claim any job.</>}
        />
      </div>

      {/* Notary-only enrichment — alert preferences + signing-agent credentials. Never a required step. */}
      {["notary", "both"].includes(user?.service_capability) && (
        <div className="space-y-4" data-testid="notary-enrichment">
          <NotaryAlertsToggle />
          <SigningAgentCard />
        </div>
      )}

      {/* Onboarding step sequence */}
      {data.steps && <StepIndicator steps={data.steps} />}

      {/* Provider Agreement — required eligibility gate (ARPS-track providers only) */}
      {agr && agr.required && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="provider-agreement-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-slate-400" />
              <p className="text-sm font-semibold text-slate-700">{agr.title}</p>
            </div>
            {agr.accepted
              ? <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="provider-agreement-status"><CheckCircle2 className="h-3 w-3" /> Accepted</span>
              : agr.satisfied
                ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600" data-testid="provider-agreement-status">{agr.status_label || "Grandfathered"}</span>
                : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid="provider-agreement-status">Acceptance required</span>}
          </div>
          {agr.legally_final === false && (
            <p className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700" data-testid="provider-agreement-unavailable">This agreement is temporarily unavailable for acceptance. Please check back shortly.</p>
          )}
          {agr.sections ? (
            <div className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-4" data-testid="provider-agreement-content">
              {(agr.sections || []).map((sec, i) => (
                <div key={i} className="mb-4">
                  <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-700">{sec.heading}</p>
                  {(sec.paragraphs || []).map((p, j) => (
                    <p key={j} className="mb-1.5 text-xs leading-relaxed text-slate-600">{p}</p>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <pre className="mt-3 max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50 p-4 font-sans text-xs leading-relaxed text-slate-600" data-testid="provider-agreement-content">{agr.content}</pre>
          )}
          {agr.accepted ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-slate-400" data-testid="provider-agreement-record">
                Accepted by {agr.signer || "you"} · {agr.accepted_at ? new Date(agr.accepted_at).toLocaleString() : ""}{agr.effective_date ? ` · Effective ${agr.effective_date}` : ""}{agr.ip_address ? ` · IP ${agr.ip_address}` : ""}
              </p>
              {agr.mode === "v1" && (
                <Button size="sm" variant="outline" data-testid="provider-agreement-download-btn" onClick={downloadAgreement} className="h-8">
                  <Download className="mr-1 h-3.5 w-3.5" /> Download my copy (PDF)
                </Button>
              )}
            </div>
          ) : agr.satisfied ? (
            <p className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-500" data-testid="provider-agreement-grandfathered">
              You're covered under your existing independent-contractor agreement on file — no action is needed. This new agreement applies to providers onboarding going forward.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              <label className="flex items-start gap-2 text-xs text-slate-600" htmlFor="pa-agree">
                <Checkbox id="pa-agree" data-testid="provider-agreement-agree-checkbox" checked={agreeChecked} onCheckedChange={(v) => setAgreeChecked(!!v)} className="mt-0.5" />
                <span>I have read and understand this Provider Agreement, and I agree to its terms as an independent contractor.</span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <Input data-testid="provider-agreement-sign-input" placeholder="Type full legal name to sign" value={icSig} onChange={(e) => setIcSig(e.target.value)} className="h-9 w-64" />
                <Button data-testid="provider-agreement-agree-btn" size="sm" disabled={signingIc || !agreeChecked || !icSig.trim() || agr.legally_final === false} onClick={signIc} className="bg-emerald-600 text-white hover:bg-emerald-700">
                  {signingIc ? "Recording…" : "I Agree"}
                </Button>
              </div>
              <p className="text-[11px] text-slate-400">Your typed legal name (as your e-signature), the document version, the date/time, and your IP address are recorded when you accept. This step cannot be skipped.</p>
            </div>
          )}
        </div>
      )}

      {/* Stripe Connect Setup — required payout onboarding */}
      {data.connect && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="connect-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-slate-400" />
              <p className="text-sm font-semibold text-slate-700">{cn.label || "Stripe Connect Setup"}</p>
              {cn.required && !cn.enabled && <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">Required</span>}
            </div>
            {cn.payouts_enabled
              ? <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="connect-status"><CheckCircle2 className="h-3 w-3" /> Payouts enabled</span>
              : cn.grandfathered
              ? <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700" data-testid="connect-status">Setup recommended</span>
              : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid="connect-status">{cn.status === "pending" ? "In review" : "Setup required"}</span>}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-slate-500" data-testid="connect-message">{cn.grandfathered && !cn.payouts_enabled ? "You're eligible for assignments, but you must complete Stripe payout setup to cash out your completed jobs." : cn.message}</p>
          {!cn.payouts_enabled && (
            <div className="mt-3">
              <Button data-testid="connect-setup-btn" size="sm" disabled={connecting} onClick={startConnect} className="bg-indigo-600 text-white hover:bg-indigo-700">
                {connecting ? "Opening Stripe…" : cn.has_account ? "Continue payout setup" : "Set up payouts"}
              </Button>
              <p className="mt-2 text-[11px] text-slate-400">You'll be redirected to Stripe to verify your identity and add a payout account.{cn.grandfathered ? "" : " You must complete this to be eligible for assignments."}</p>
            </div>
          )}
          {cn.payouts_enabled && (
            <p className="mt-2 text-[11px] text-emerald-600">{cn.instant_eligible ? "Instant payouts available on an eligible debit card." : "Standard weekly deposits enabled. Add an eligible debit card in Stripe for instant payouts."}</p>
          )}
        </div>
      )}

      {/* Overview */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="onboarding-account-card">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Account</p>
            {accountComplete
              ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              : <AlertCircle className="h-5 w-5 text-amber-500" />}
          </div>
          <p className="mt-2 font-display text-lg font-bold text-slate-900" data-testid="account-complete-status">
            {accountComplete ? "Complete" : "Incomplete"}
          </p>
          {!accountComplete && <p className="mt-1 text-xs text-amber-600">Missing: {data.account.missing_fields.join(", ")}</p>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="onboarding-docs-card">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Documents approved</p>
            <FileCheck2 className="h-5 w-5 text-slate-400" />
          </div>
          <p className="mt-2 font-display text-lg font-bold text-slate-900" data-testid="docs-approved-count">
            {s.approved} / {data.total_documents}
          </p>
          <div className="mt-2"><ProgressBar value={s.approved} total={data.total_documents} testid="docs-progress" /></div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="onboarding-approval-card">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Account approval</p>
            <ShieldCheck className="h-5 w-5 text-slate-400" />
          </div>
          <div className="mt-2"><ServerStatusBadge status={data.server_status} testid="server-approval-status" /></div>
          <p className="mt-2 text-xs text-slate-400">Account approval is separate from document approval.</p>
        </div>
      </div>

      {/* Profile completion */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="profile-section">
        <h3 className="font-display text-lg font-bold text-slate-900">Your profile</h3>
        <p className="text-sm text-slate-500">Required: full legal name, phone, and full address (street, city, state, ZIP).</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><Label>Full legal name *</Label><Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
          <div><Label>Phone *</Label><Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Street Address *</Label><Input data-testid="profile-street" value={profile.street} onChange={(e) => setProfile({ ...profile, street: e.target.value })} /></div>
          <div><Label>City *</Label><Input data-testid="profile-city" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} /></div>
          <div><Label>State *</Label>
            <select data-testid="profile-state" value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select state</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>ZIP Code *</Label><Input data-testid="profile-zip" value={profile.zip} onChange={(e) => setProfile({ ...profile, zip: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>License / credential number</Label><Input data-testid="profile-license" value={profile.license_number} onChange={(e) => setProfile({ ...profile, license_number: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Service areas — counties you serve (comma-separated) *</Label><Input data-testid="profile-coverage" value={profile.coverage_counties} onChange={(e) => setProfile({ ...profile, coverage_counties: e.target.value })} placeholder="e.g. Shelby, Davidson, Knox" /><p className="mt-1 text-xs text-slate-500">You'll only see and be able to claim process-server jobs in these counties.</p></div>
        </div>
        <Button data-testid="save-profile-button" onClick={saveProfile} disabled={savingProfile} className="mt-4 bg-slate-900 text-white hover:bg-slate-800">
          {savingProfile ? "Saving…" : "Save profile"}
        </Button>
      </div>

      {/* Executed Subscriber Agreement (Exhibit E auto-filled) */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="exec-agreement-section">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">Your ARPS Platform Subscriber Agreement</h3>
            <p className="text-sm text-slate-500">View your executed agreement with Exhibit E filled in from your account details.</p>
          </div>
          <Button variant="outline" data-testid="view-my-exec-agreement" onClick={loadExecAgr}>View executed agreement</Button>
        </div>
      </div>
      {execAgr && (
        <div data-testid="my-exec-agreement-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setExecAgr(null)}>
          <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h3 className="font-display text-lg font-bold text-slate-900">{execAgr.title} <span className="text-xs font-normal text-slate-400">Version {execAgr.version}</span></h3>
              <button data-testid="close-my-exec-agreement" onClick={() => setExecAgr(null)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <pre data-testid="my-exec-agreement-content" className="flex-1 overflow-auto whitespace-pre-wrap p-4 font-sans text-xs leading-relaxed text-slate-700">{execAgr.content}</pre>
          </div>
        </div>
      )}

      {/* Document center */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="document-center">
        <div className="border-b border-slate-100 px-6 py-4">
          <h3 className="font-display text-lg font-bold text-slate-900">Required documents</h3>
          <p className="text-sm text-slate-500">Upload each credential. Documents are never auto-approved — an admin reviews them.</p>
        </div>
        <div className="divide-y divide-slate-100">
          {data.documents.map((d) => (
            <div key={d.key} data-testid={`doc-row-${d.key}`} className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">{d.label}{d.optional && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Optional</span>}</p>
                {d.filename && <p className="truncate text-xs text-slate-400">{d.filename}</p>}
                {d.status === "rejected" && d.admin_notes && <p className="text-xs text-red-600">Reviewer note: {d.admin_notes}</p>}
              </div>
              <div className="flex items-center gap-3">
                <DocStatusBadge status={d.status} testid={`doc-status-${d.key}`} />
                {d.file_id && (
                  <>
                    <a data-testid={`doc-view-${d.key}`} href={`${API}/server/documents/${d.key}/download?disposition=inline`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline">
                      <Eye className="h-3.5 w-3.5" /> View
                    </a>
                    <a data-testid={`doc-download-${d.key}`} href={`${API}/server/documents/${d.key}/download?disposition=attachment`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:underline">
                      <Download className="h-3.5 w-3.5" />
                    </a>
                  </>
                )}
                <input ref={(el) => (fileRefs.current[d.key] = el)} type="file" className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif"
                  onChange={(e) => upload(d.key, e.target.files?.[0])} data-testid={`doc-file-${d.key}`} />
                <Button size="sm" variant="outline" disabled={uploadingKey === d.key}
                  onClick={() => fileRefs.current[d.key]?.click()} data-testid={`doc-upload-${d.key}`}>
                  <Upload className="mr-1 h-3.5 w-3.5" />
                  {uploadingKey === d.key ? "Uploading…" : d.status === "missing" ? "Upload" : "Replace"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
